import React, { useState, useEffect } from 'react';
import { learnerProgressService } from '../../services/learnerProgressService';
import { srsService } from '../../services/srsService';
import { fireCelebrationConfetti } from '../../utils/confettiHelper';
import { useUserStore } from '../../stores/useUserStore';
import VoiceService from '../../services/voiceService';
import HoverWord, { renderParsedText } from '../../components/common/HoverWord';
import { Mic, MicOff, Volume2, Sparkles, Send, CheckCircle2, MessageSquare, RefreshCw, Trophy, AlertCircle, PlayCircle, HelpCircle, Layers, BookOpen, Star, Radio, AlertTriangle, Info, ShoppingBag, Zap } from 'lucide-react';
import GlobalModeToggle from '../../components/cambridge/GlobalModeToggle';
import InformationExchangeP2 from '../../components/cambridge/InformationExchangeP2';
import NovaMascotStore from '../../components/mascot/NovaMascotStore';
import CompletionModal from '../../components/common/CompletionModal';
import AIDebateMode from '../../components/cambridge/AIDebateMode';

/**
 * Cambridge Speech Assessment Engine
 * - Evaluates Key Chunks & Target Vocabulary match against STT transcript.
 * - Audio duration is STRICTLY a binary validity gate (attempted vs empty), NEVER a score quality proxy.
 */
const calculateSpeechAccuracy = (spokenText, targetText, keyChunks = []) => {
  if (!spokenText || !spokenText.trim()) {
    return { accuracyScore: 0, fluencyScore: 0, stars: 0, isAttempted: false };
  }

  const cleanSpoken = spokenText.toLowerCase().replace(/[^\w\s']/g, ' ');
  const spokenTokens = cleanSpoken.split(/\s+/).filter(Boolean);

  if (spokenTokens.length === 0) {
    return { accuracyScore: 0, fluencyScore: 0, stars: 0, isAttempted: false };
  }

  // 1. Target words token matching
  const cleanTarget = (targetText || '').toLowerCase().replace(/[^\w\s']/g, ' ');
  const targetTokens = cleanTarget.split(/\s+/).filter(Boolean);

  let matchedWords = 0;
  targetTokens.forEach((word) => {
    if (spokenTokens.includes(word)) matchedWords++;
  });
  const wordScore = targetTokens.length > 0 ? (matchedWords / targetTokens.length) * 100 : 50;

  // 2. Key Chunks evaluation (higher weight for Cambridge phrase mastery)
  let chunkScore = 100;
  if (Array.isArray(keyChunks) && keyChunks.length > 0) {
    let matchedChunks = 0;
    keyChunks.forEach((chunk) => {
      const cleanChunk = chunk.toLowerCase().trim();
      if (cleanSpoken.includes(cleanChunk)) {
        matchedChunks++;
      } else {
        const chunkWords = cleanChunk.split(/\s+/);
        const subMatch = chunkWords.filter(w => spokenTokens.includes(w)).length;
        if (subMatch / chunkWords.length >= 0.7) matchedChunks += 0.8;
      }
    });
    chunkScore = (matchedChunks / keyChunks.length) * 100;
  }

  const finalScore = Math.min(100, Math.round(keyChunks.length > 0 ? (wordScore * 0.4 + chunkScore * 0.6) : wordScore));
  const stars = finalScore >= 80 ? 3 : finalScore >= 50 ? 2 : finalScore >= 25 ? 1 : 0;

  return {
    accuracyScore: finalScore,
    fluencyScore: Math.min(100, Math.round(finalScore * 0.95)),
    stars,
    isAttempted: true
  };
};

import FindDifferencesInteractive from '../../components/cambridge/FindDifferencesInteractive';
import ExamIntroAudioButton from '../../components/common/ExamIntroAudioButton';

export default function NovaTalkShowHub({ data, weekNumber }) {
  const currentUser = useUserStore((state) => state.currentUser);
  const learnerId = currentUser?.id || currentUser?.username || 'guest_01';
  const addXP = useUserStore((state) => state.addXP);
  const userXP = useUserStore((state) => state.userXP || 0);

  const [subMode, setSubMode] = useState('podcast'); // 'podcast' | 'talkshow' | 'cue_card' | 'pic_story' | 'find_diff'
  const [activeMode, setActiveMode] = useState('learn'); // 'learn' | 'check'
  const [showMascotStore, setShowMascotStore] = useState(false);

  // Check Mode Rotation: 1 Speaking Part per week, cycling every 4 weeks
  // W33→Q&A, W34→PicStory, W35→Exchange, W36→FindDiff, W37→Q&A...
  const CHECK_MODE_ROTATION = ['talkshow', 'pic_story', 'cue_card', 'find_diff'];
  const CHECK_MODE_LABELS = {
    talkshow: { icon: '💬', label: 'Personal Q&A', part: 'Part 1' },
    pic_story: { icon: '🖼️', label: 'Picture Story', part: 'Part 3' },
    cue_card: { icon: '❓', label: 'Information Exchange', part: 'Part 2' },
    find_diff: { icon: '🔍', label: 'Find Differences', part: 'Part 4' },
  };
  const checkModeSubMode = CHECK_MODE_ROTATION[(weekNumber - 1) % 4];

  // Auto-switch subMode when entering/leaving Check Mode
  useEffect(() => {
    if (activeMode === 'check') {
      setSubMode(checkModeSubMode);
    }
  }, [activeMode, checkModeSubMode]);

  const [shadowingPhase, setShadowingPhase] = useState(1); // Phase 1: 5 Sentences | Phase 2: Long Paragraph
  const [isRecording, setIsRecording] = useState(false);
  const [podcastScore, setPodcastScore] = useState(null);
  const [showPracticeNotice, setShowPracticeNotice] = useState(false);

  // Nova Live Talk Show State Machine (5 Turns)
  const [currentTurnIdx, setCurrentTurnIdx] = useState(0);
  const [isMicListening, setIsMicListening] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [userSpeechInput, setUserSpeechInput] = useState('');
  const [isTalkshowEnded, setIsTalkshowEnded] = useState(false);

  // Cambridge Speaking Part 2: Cue-Card Question Master (Reverse Role) State
  const [cueCardIdx, setCueCardIdx] = useState(0);
  const [cueQuestionInput, setCueQuestionInput] = useState('');
  const [cueFeedback, setCueFeedback] = useState(null);
  const [cueScore, setCueScore] = useState(null);
  const [cueCompleted, setCueCompleted] = useState(false);
  const [cueFailedAttempts, setCueFailedAttempts] = useState(0);

  // Cambridge Speaking Part 3: 4-Picture Story Continuation State
  const [picStoryStep, setPicStoryStep] = useState(1);
  const [picStoryRecording, setPicStoryRecording] = useState(false);
  const [picStoryFeedback, setPicStoryFeedback] = useState({});
  const [picStoryScore, setPicStoryScore] = useState(null);
  const [activeRecordingPicId, setActiveRecordingPicId] = useState(null);
  const [pictureScores, setPictureScores] = useState({});

  const handleSelectPicStorySubmode = () => {
    setSubMode('pic_story');
    // Direct User Gesture audio trigger — unblocks browser Autoplay Policy 100%
    speakNovaQuestion(pictureStoryData.intro_audio_text);
  };

  const handleRecordPicture = async (picId) => {
    if (activeRecordingPicId === picId && isMicListening) {
      setIsMicListening(false);
      setActiveRecordingPicId(null);

      const currentPic = pictureStoryData.pictures.find(p => p.id === picId);
      const targetText = currentPic?.target_text || currentPic?.script || currentPic?.prompt_en || "";
      const keyChunks = currentPic?.key_chunks || [];
      const evalRes = calculateSpeechAccuracy(userSpeechInput, targetText, keyChunks);

      setPictureScores(prev => ({
        ...prev,
        [picId]: { stars: evalRes.stars, score: evalRes.accuracyScore, isAttempted: evalRes.isAttempted }
      }));

      // BUG-2 FIX: Only log & celebrate if student actually attempted (isAttempted=true)
      // Prevents empty/noise recordings from polluting learner progress data
      if (evalRes.isAttempted) {
        if (evalRes.accuracyScore >= 80) fireCelebrationConfetti();
        await learnerProgressService.logAttempt({
          learnerId,
          contentId: `w${weekNumber}_speaking_p3_picture_${picId}`,
          mode: 'learn',
          result: evalRes.accuracyScore >= 60 ? 'correct' : 'incorrect',
          score: evalRes.accuracyScore,
          timeSpentSeconds: 45
        });
      }
    } else {
      setActiveRecordingPicId(picId);
      handleMicClick();
    }
  };



  const sentencesList = data?.shadowing_sentences || [];

  const longParagraph = data?.podcast_shadowing?.long_paragraph || data?.long_paragraph || {
    title: "Story Podcast",
    text: "",
    phonetic_guide: ""
  };

  const srsSpeakingContext = srsService.getSpeakingContextualPrompt();

  const talkshowTurns = data?.talkshow_turns || [];

  const cueCardPrompts = data?.cue_card_prompts || [];

  const pictureStoryData = data?.picture_story_continuation || {
    title: "Picture Story",
    intro_audio_text: "",
    pictures: []
  };

  const handleAskNovaQuestion = async (userQuestion) => {
    const currentCue = cueCardPrompts[cueCardIdx];
    const cleanUserQ = (userQuestion || '').trim().toLowerCase();
    
    // Validate question syntax (Starts with question word or auxiliary)
    const validStart = ['where', 'why', 'who', 'what', 'how', 'when', 'is', 'was', 'did', 'does', 'can', 'could'].some(w => cleanUserQ.startsWith(w));
    const hasQuestionMark = userQuestion.includes('?') || userQuestion.length > 10;
    
    const isCorrectSyntax = validStart && hasQuestionMark;

    if (!isCorrectSyntax) {
      setCueFailedAttempts(prev => prev + 1);
    } else {
      setCueFailedAttempts(0);
    }
    
    setCueFeedback({
      isCorrectSyntax,
      userQuestion,
      novaResponse: currentCue.nova_answer_audio_text
    });

    // Nova speaks speech response
    speakNovaQuestion(currentCue.nova_answer_audio_text);

    if (isCorrectSyntax) {
      if (cueCardIdx < cueCardPrompts.length - 1) {
        setTimeout(() => {
          setCueCardIdx(prev => prev + 1);
          setCueQuestionInput('');
          setCueFeedback(null);
          setCueFailedAttempts(0);
        }, 4000);
      } else {
        setCueCompleted(true);
        await learnerProgressService.logAttempt({
          learnerId,
          contentId: `w${weekNumber}_speaking_p2_cue_card`,
          mode: 'learn',
          result: 'correct',
          score: 100,
          timeSpentSeconds: 60
        });
      }
    }
  };

  useEffect(() => {
    if (subMode === 'talkshow' && chatHistory.length === 0 && talkshowTurns[0]) {
      const q1 = talkshowTurns[0].nova_question;
      setChatHistory([{ sender: 'nova', text: q1 }]);
      speakNovaQuestion(q1);
    }
  }, [subMode]);

  const speakNovaQuestion = async (text) => {
    try {
      await VoiceService.speak(text, 'questions');
    } catch (err) {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  const handlePlaySentence = async (text) => {
    try {
      await VoiceService.speak(text, 'shadowing');
    } catch (err) {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  const handleToggleRecording = async () => {
    if (!isRecording) {
      setIsRecording(true);
      setPodcastScore(null);
    } else {
      setIsRecording(false);
      const targetStr = shadowingPhase === 1 ? (sentencesList[0]?.text || '') : (longParagraph?.text || '');
      const realScores = calculateSpeechAccuracy(userSpeechInput, targetStr);

      setPodcastScore({
        stars: realScores.stars,
        accuracyScore: realScores.accuracyScore,
        fluencyScore: realScores.fluencyScore,
        verificationStatus: realScores.stars === 3 ? '3 Stars (Gold)' : realScores.stars === 2 ? '2 Stars (Silver)' : '1 Star (Pass)'
      });

      await learnerProgressService.logAttempt({
        learnerId,
        contentId: `w${weekNumber}_podcast_shadowing_phase${shadowingPhase}`,
        mode: 'learn',
        result: realScores.accuracyScore >= 60 ? 'correct' : 'incorrect',
        score: realScores.accuracyScore,
        timeSpentSeconds: 60
      });
    }
  };

  const [showThinkingBadge, setShowThinkingBadge] = useState(false);
  const thinkingTimerRef = React.useRef(null);
  const vadTimerRef = React.useRef(null);

  const handleMicClick = () => {
    if (isMicListening) {
      setIsMicListening(false);
      setShowThinkingBadge(false);
      if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
      if (vadTimerRef.current) clearTimeout(vadTimerRef.current);
      return;
    }

    setIsMicListening(true);
    setShowThinkingBadge(false);

    // Hesitation indicator badge after 2000ms pause
    thinkingTimerRef.current = setTimeout(() => {
      setShowThinkingBadge(true);
    }, 2000);

    // Adaptive VAD 5000ms silence timeout
    vadTimerRef.current = setTimeout(() => {
      setIsMicListening(false);
      setShowThinkingBadge(false);
    }, 5000);

    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.interimResults = false;

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setUserSpeechInput(transcript);
        setIsMicListening(false);
        setShowThinkingBadge(false);
        if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
        if (vadTimerRef.current) clearTimeout(vadTimerRef.current);
      };

      recognition.onerror = () => {
        setIsMicListening(false);
        setShowThinkingBadge(false);
        if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
        if (vadTimerRef.current) clearTimeout(vadTimerRef.current);
      };

      recognition.onend = () => {
        setIsMicListening(false);
        setShowThinkingBadge(false);
        if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
        if (vadTimerRef.current) clearTimeout(vadTimerRef.current);
      };

      recognition.start();
    } else {
      setTimeout(() => {
        setIsMicListening(false);
        setShowThinkingBadge(false);
      }, 5000);
    }
  };

  const handleSendTalkshowMessage = async (userText) => {
    if (isTalkshowEnded) return;
    const currentInput = userText || userSpeechInput || "I answered the question.";

    const newHistory = [...chatHistory, { sender: 'user', text: currentInput }];
    const nextIdx = currentTurnIdx + 1;

    if (nextIdx >= talkshowTurns.length) {
      setChatHistory([...newHistory, { sender: 'nova', text: "Fantastic job! You completed all 5 talk show turns with Nova!" }]);
      setIsTalkshowEnded(true);
      await learnerProgressService.logAttempt({
        learnerId,
        contentId: `w${weekNumber}_nova_talkshow`,
        mode: 'learn',
        result: 'correct',
        score: 100,
        timeSpentSeconds: 120
      });
      return;
    }

    setCurrentTurnIdx(nextIdx);
    const nextQ = talkshowTurns[nextIdx].nova_question;
    setChatHistory([...newHistory, { sender: 'nova', text: nextQ }]);
    setUserSpeechInput('');
    speakNovaQuestion(nextQ);
  };

  return (
    <div className="w-full max-w-5xl mx-auto p-4 sm:p-6 bg-white text-slate-800 rounded-3xl border border-slate-200 shadow-xl font-sans">
      {/* Top Bar Controls: Mode Toggle & Mascot Store */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <GlobalModeToggle activeMode={activeMode} onModeChange={setActiveMode} />

        <button
          onClick={() => setShowMascotStore(true)}
          className="px-3.5 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl text-xs font-black transition flex items-center gap-1.5 shadow-md"
        >
          <ShoppingBag size={14} className="text-amber-300" /> Nova Store ({userXP} XP)
        </button>
      </div>

      {/* Check Mode Exam Condition Banner */}
      {activeMode === 'check' && (
        <div className="p-5 mb-6 bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 border-2 border-purple-400 rounded-3xl text-white shadow-xl animate-in fade-in space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl animate-bounce">🤖</span>
              <h3 className="text-base font-black text-amber-300">🎯 EXAMINER NOVA SPEAKING MOCK ROOM</h3>
            </div>
            <span className="text-[10px] font-black uppercase px-3 py-1 bg-purple-500 text-white rounded-full tracking-wider shadow-sm">Real Exam (No Scripts)</span>
          </div>
          <p className="text-xs font-bold text-slate-200 leading-relaxed">
            Examiner Mascot Nova will ask you questions directly via Audio. Answer directly into your microphone under Cambridge Flyers Examination conditions with zero text scripts or hints!
          </p>
        </div>
      )}

      {/* Sub-Mode Switcher: Hidden in Check Mode (single task per week) */}
      {activeMode === 'check' ? (
        <div className="mb-4 p-4 bg-purple-50 border-2 border-purple-200 rounded-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{CHECK_MODE_LABELS[checkModeSubMode]?.icon}</span>
            <div>
              <span className="text-[10px] font-black text-purple-600 uppercase tracking-widest block">
                Weekly Check — {CHECK_MODE_LABELS[checkModeSubMode]?.part}
              </span>
              <h3 className="text-sm font-black text-purple-950">
                {CHECK_MODE_LABELS[checkModeSubMode]?.label}
              </h3>
            </div>
          </div>
          <span className="px-3 py-1 bg-purple-200 text-purple-900 text-[10px] font-black rounded-full">
            ~5-7 min
          </span>
        </div>
      ) : (
      <div className="w-full p-2 bg-slate-100/90 rounded-2xl border border-slate-200 shadow-inner mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 w-full">
          <button
            type="button"
            onClick={() => setSubMode('podcast')}
            className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
              subMode === 'podcast' ? 'bg-rose-600 text-white shadow-md ring-2 ring-rose-300' : 'bg-white text-slate-700 hover:bg-rose-50 border border-slate-200'
            }`}
          >
            🎙️ Podcast
          </button>
          <button
            type="button"
            onClick={() => setSubMode('talkshow')}
            className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
              subMode === 'talkshow' ? 'bg-purple-600 text-white shadow-md ring-2 ring-purple-300' : 'bg-white text-slate-700 hover:bg-purple-50 border border-slate-200'
            }`}
          >
            💬 Personal Q&A
          </button>
          <button
            type="button"
            onClick={() => setSubMode('cue_card')}
            className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
              subMode === 'cue_card' ? 'bg-amber-600 text-white shadow-md ring-2 ring-amber-300' : 'bg-white text-amber-900 border border-amber-300 hover:bg-amber-50'
            }`}
          >
            ❓ Ask & Answer
          </button>
          <button
            type="button"
            onClick={handleSelectPicStorySubmode}
            className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
              (subMode === 'pic_story' || subMode === 'story_picture') ? 'bg-indigo-600 text-white shadow-md ring-2 ring-indigo-300' : 'bg-white text-slate-700 hover:bg-indigo-50 border border-slate-200'
            }`}
          >
            🖼️ Picture Story
          </button>
          <button
            type="button"
            onClick={() => setSubMode('find_diff')}
            className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
              subMode === 'find_diff' ? 'bg-rose-600 text-white shadow-md ring-2 ring-rose-300' : 'bg-white text-rose-900 border border-rose-300 hover:bg-rose-50'
            }`}
          >
            🔍 Find Diff
          </button>
          <button
            type="button"
            onClick={() => setSubMode('ai_debate')}
            className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
              subMode === 'ai_debate' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md ring-2 ring-purple-300' : 'bg-white text-purple-900 border border-purple-300 hover:bg-purple-50'
            }`}
          >
            🎤 AI Debate
          </button>
        </div>
      </div>
      )}

      {subMode === 'ai_debate' ? (
        <AIDebateMode
          debateTopics={data?.debate_topics}
          weekNumber={weekNumber}
        />
      ) : subMode === 'find_diff' ? (
        <FindDifferencesInteractive
          customData={data?.find_differences}
          isStealthMode={activeMode === 'check'}
          onComplete={async (score) => {
            await learnerProgressService.logAttempt({
              learnerId,
              contentId: `w${weekNumber}_speaking_p1_differences`,
              mode: 'learn',
              result: score >= 80 ? 'correct' : 'incorrect',
              score,
              timeSpentSeconds: 60
            });
          }}
        />
      ) : subMode === 'podcast' ? (
        /* MODE 1: PODCAST SHADOWING (2 PHASES) */
        <div className="space-y-6">
          {/* Phase Selector Tabs */}
          <div className="flex items-center gap-3 bg-indigo-50/70 p-2 rounded-2xl border border-indigo-200">
            <button
              onClick={() => setShadowingPhase(1)}
              className={`flex-1 py-2.5 rounded-xl text-xs font-black transition flex items-center justify-center gap-2 ${
                shadowingPhase === 1 ? 'bg-indigo-600 text-white shadow-md' : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100'
              }`}
            >
              <Layers size={14} /> Phase 1: 5 Single Sentences
            </button>
            <button
              onClick={() => setShadowingPhase(2)}
              className={`flex-1 py-2.5 rounded-xl text-xs font-black transition flex items-center justify-center gap-2 ${
                shadowingPhase === 2 ? 'bg-indigo-600 text-white shadow-md' : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100'
              }`}
            >
              <BookOpen size={14} /> Phase 2: Continuous Story
            </button>
          </div>

          {shadowingPhase === 1 ? (
            /* Phase 1: 5 Single Sentences */
            <div className="space-y-3">
              <div className="text-xs font-black text-slate-400 uppercase tracking-wider">
                Phase 1 — Listen and repeat each of the 5 sentences:
              </div>
              {sentencesList.map((turn, idx) => (
                <div
                  key={turn.id || idx}
                  className="p-4 bg-slate-50 rounded-2xl border border-slate-200 shadow-sm flex items-start justify-between gap-4"
                >
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-0.5 bg-indigo-100 text-indigo-800 text-[10px] font-black rounded-md uppercase">
                        Sentence {idx + 1} ({turn.speaker || 'Tom'})
                      </span>
                    </div>
                    <p className="text-sm font-extrabold text-slate-900 leading-relaxed">
                      {activeMode === 'check' ? (
                        <span className="text-indigo-600 font-mono italic text-xs">
                          🎧 [Cambridge Real Exam Mode — Audio Prompt Only (Script Hidden)]
                        </span>
                      ) : (
                        renderParsedText(turn.text, 'indigo')
                      )}
                    </p>
                    {turn.phonetic_guide && (
                      <p className="text-[11px] font-mono text-slate-400 font-medium">
                        [{turn.phonetic_guide}]
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handlePlaySentence(turn.text)}
                    className="p-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold transition flex items-center gap-1 shrink-0 border border-indigo-200"
                    title="Listen to phrase"
                  >
                    <Volume2 size={16} /> Play
                  </button>
                </div>
              ))}
            </div>
          ) : (
            /* Phase 2: Continuous Story Intonation Shadowing */
            <div className="p-6 bg-indigo-50 rounded-3xl border border-indigo-200 space-y-4 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-black text-indigo-950">{renderParsedText(longParagraph.title, 'indigo')}</h3>
                <button
                  onClick={() => handlePlaySentence(longParagraph.text)}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-xl text-xs transition flex items-center gap-1.5 shadow-md"
                >
                  <Volume2 size={16} /> Play Full Story
                </button>
              </div>
              <p className="text-base font-extrabold text-indigo-950 leading-relaxed p-4 bg-white rounded-2xl border border-indigo-100 shadow-inner">
                {activeMode === 'check' ? (
                  <span className="text-indigo-600 font-mono italic text-xs block text-center py-4">
                    🎧 [Cambridge Real Exam Mode — Audio Prompt Only (Full Story Script Hidden)]
                  </span>
                ) : (
                  renderParsedText(longParagraph.text, 'indigo')
                )}
              </p>
              <p className="text-xs font-mono text-indigo-700 italic">{longParagraph.phonetic_guide}</p>
            </div>
          )}

          {/* Recording Action Button */}
          <div className="flex flex-col items-center justify-center py-4">
            <button
              onClick={handleToggleRecording}
              className={`px-8 py-4 rounded-2xl text-sm font-black transition flex items-center gap-2 shadow-lg ${
                isRecording ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse' : 'bg-indigo-600 hover:bg-indigo-700 text-white'
              }`}
            >
              <Mic size={18} /> {isRecording ? 'Stop Recording (Evaluating Shadowing...)' : 'Start Full Shadowing Recording'}
            </button>
            <p className="text-xs font-semibold text-slate-500 mt-2 flex items-center gap-1">
              🎧 <span>Please wear headphones during shadowing for the clearest recording!</span>
            </p>
          </div>

          {podcastScore && (
            <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200 text-center animate-in fade-in">
              <div className="flex justify-center items-center gap-1 text-amber-500 mb-2">
                {[...Array(podcastScore.stars)].map((_, i) => (
                  <Star key={i} size={20} className="fill-amber-400" />
                ))}
              </div>
              <h4 className="text-base font-black text-emerald-950">Pronunciation Score: {podcastScore.accuracyScore}%</h4>
              <p className="text-xs text-emerald-700 font-semibold mt-1">Fluency: {podcastScore.fluencyScore}%</p>
            </div>
          )}
        </div>
      ) : subMode === 'talkshow' ? (
        /* MODE 2: NOVA LIVE TALK SHOW (EXACTLY 5 TURNS WITH BIG MIC BUTTON) */
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs font-black text-slate-700">
            <span className="flex items-center gap-1">
              <Radio size={14} className="text-purple-600" /> Turn {currentTurnIdx + 1} / {talkshowTurns.length}
            </span>
            <ExamIntroAudioButton
              weekNumber={weekNumber}
              introId="exam_intro_S4"
              introText="Now let's talk about you and your daily life. Answer the questions."
            />
          </div>

          {/* Chat History Container */}
          <div className="h-80 overflow-y-auto p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3 shadow-inner">
            {chatHistory.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-md p-3.5 rounded-2xl text-xs font-semibold leading-relaxed ${
                    msg.sender === 'user'
                      ? 'bg-indigo-600 text-white rounded-br-none shadow-sm'
                      : 'bg-white text-slate-900 border border-slate-200 rounded-bl-none shadow-sm'
                  }`}
                >
                  <div className="text-[9px] font-black uppercase mb-1 opacity-75">
                    {msg.sender === 'user' ? 'You' : 'Nova AI Examiner'}
                  </div>
                  {msg.text}
                </div>
              </div>
            ))}
          </div>

          {/* Large Central Microphone & Input Controls */}
          {!isTalkshowEnded ? (
            <div className="flex flex-col gap-2 pt-2">
              {showThinkingBadge && (
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-2xl text-xs font-semibold animate-pulse shadow-sm">
                  <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
                  <span>Nova vẫn đang lắng nghe con nè, cứ thong thả nói tiếp nhé!</span>
                </div>
              )}
              <div className="flex items-center gap-3">
              {/* Large Microphone Recording Button */}
              <button
                onClick={handleMicClick}
                className={`p-4 rounded-2xl text-white font-black transition flex items-center justify-center gap-2 shadow-lg shrink-0 ${
                  isMicListening
                    ? 'bg-red-600 animate-pulse ring-4 ring-red-300'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
                title="Click to speak your answer with Microphone"
              >
                {isMicListening ? <MicOff size={22} /> : <Mic size={22} />}
                <span className="text-xs font-black hidden sm:inline">
                  {isMicListening ? 'Listening...' : 'Speak via Mic'}
                </span>
              </button>

              <input
                type="text"
                value={userSpeechInput}
                onChange={(e) => setUserSpeechInput(e.target.value)}
                placeholder={activeMode === 'check' ? "🎧 Listen to Examiner Nova & Speak your answer into Mic..." : "Type or click Mic button to speak your answer..."}
                onKeyDown={(e) => e.key === 'Enter' && handleSendTalkshowMessage(userSpeechInput)}
                className="flex-1 p-4 bg-slate-50 text-slate-900 rounded-2xl border border-slate-300 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-purple-500 shadow-inner"
              />

              <button
                onClick={() => handleSendTalkshowMessage(userSpeechInput)}
                className="px-6 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-black shadow-md transition shrink-0"
              >
                Send Answer
              </button>
            </div>
          </div>
          ) : (
            <div className="p-6 bg-emerald-50 border border-emerald-300 rounded-2xl text-center space-y-2 animate-in fade-in">
              <h4 className="text-base font-black text-emerald-950">5-Turn Talk Show Exam Completed!</h4>
              <p className="text-xs font-bold text-emerald-700">All 5 turns answered successfully. Retelling score logged to progress service.</p>
              <p className="text-[11px] font-bold text-amber-900 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200 inline-block">
                💡 Điểm luyện tập AI — Chưa phải kết quả thi Cambridge chính thức đâu con nhé!
              </p>
              <button
                onClick={() => { setIsTalkshowEnded(false); setCurrentTurnIdx(0); setChatHistory([]); }}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black shadow-md transition"
              >
                Retake 5-Turn Talk Show
              </button>
            </div>
          )}
        </div>
      ) : subMode === 'cue_card' ? (
        /* MODE 3: CAMBRIDGE SPEAKING PART 2 (INFORMATION EXCHANGE SIDE-BY-SIDE TABLES) */
        <InformationExchangeP2 customData={data?.cue_card_info_exchange} weekNumber={weekNumber} isStealthMode={activeMode === 'check'} />
      ) : (
        /* MODE 4: 4-PICTURE STORY CONTINUATION (SPEAKING PART 3) */
        <div className="space-y-6">
          <div className="p-6 bg-slate-50 rounded-3xl border border-slate-200 shadow-md space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200">
              <div>
                <span className="text-xs font-black text-indigo-600 uppercase tracking-widest">🎬 PICTURE STORY MISSION</span>
                <h3 className="text-xl font-black text-slate-900">{pictureStoryData.title}</h3>
              </div>
              <span className="px-3 py-1 bg-indigo-100 text-indigo-900 text-xs font-black rounded-full font-mono">
                5 Pictures (Cambridge Standard)
              </span>
            </div>

            {/* Gamified Nova Mascot Speech Bubble & Story Intro Hero Banner */}
            <div className="p-5 sm:p-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 rounded-3xl text-white shadow-xl border-2 border-indigo-200/50 flex flex-col md:flex-row items-center gap-5 relative overflow-hidden">
              {/* Mascot Nova Avatar Badge */}
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/20 p-2 shrink-0 backdrop-blur-md border border-white/30 shadow-inner flex items-center justify-center">
                <span className="text-3xl sm:text-4xl animate-bounce">🤖</span>
              </div>

              {/* Instructions & Story Quote Context */}
              <div className="space-y-2 flex-1 text-center md:text-left">
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                  <span className="px-2.5 py-0.5 bg-amber-400 text-amber-950 font-black text-[11px] rounded-full uppercase tracking-wider shadow-sm">
                    ✨ Mascot Nova's Story Challenge
                  </span>
                  <span className="px-2.5 py-0.5 bg-white/20 text-white font-bold text-[10px] rounded-full">
                    Picture 1 Intro
                  </span>
                </div>

                <p className="text-xs sm:text-sm font-bold text-white/95 leading-relaxed">
                  "Nova has a 5-picture story for you! Click the button below to listen to the beginning (Picture 1). Then, use your mic to tell me what happens next in pictures 2, 3, 4, and 5!"
                </p>

                <p className="text-[11px] font-medium text-white/80 italic">
                  (Nova có một câu chuyện 5 bức tranh cho bạn! Bấm nút bên dưới để nghe phần mở đầu. Sau đó, dùng micro kể xem chuyện gì xảy ra tiếp theo ở các tranh 2, 3, 4 và 5 nhé!)
                </p>

                <div className="pt-2 border-t border-white/20 mt-2">
                  <span className="text-[10px] font-black uppercase tracking-wider text-amber-200">Picture 1 Story Beginning:</span>
                  <p className="text-xs sm:text-sm font-black text-white italic">
                    "{pictureStoryData.intro_audio_text}"
                  </p>
                </div>
              </div>

              {/* Prominent Action Button */}
              <button
                onClick={() => speakNovaQuestion(pictureStoryData.intro_audio_text)}
                className="w-full md:w-auto px-6 py-4 bg-white hover:bg-amber-50 text-indigo-950 rounded-2xl text-xs sm:text-sm font-black shadow-2xl transition-all active:scale-95 flex items-center justify-center gap-2.5 shrink-0 ring-4 ring-white/40 hover:ring-amber-300 group"
              >
                <Volume2 size={22} className="text-indigo-600 group-hover:scale-110 transition-transform" />
                <span className="tracking-tight">🚀 Start Mission: Listen to Story Intro 🎧</span>
              </button>
            </div>



            {/* 5 Pictures Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {pictureStoryData.pictures.map((pic) => (
                <div key={pic.id} className="p-3 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col space-y-2">
                  <div className="relative w-full h-36 bg-slate-100 rounded-xl overflow-hidden border border-slate-200">
                    <img src={pic.image} alt={pic.title} className="w-full h-full object-cover" />
                    <span className="absolute top-2 left-2 px-2 py-0.5 bg-slate-900/80 text-white text-[10px] font-black rounded-md">
                      P{pic.id}
                    </span>
                  </div>

                  <h4 className="text-xs font-black text-slate-900">{activeMode === 'check' ? `Picture ${pic.id}` : pic.title}</h4>

                  {!pic.is_intro ? (
                    <div className="pt-1 flex flex-col gap-1.5 mt-auto">
                      <button
                        onClick={() => handleRecordPicture(pic.id)}
                        className={`w-full py-2 rounded-xl text-xs font-black transition flex items-center justify-center gap-1 shadow-sm ${
                          isMicListening && activeRecordingPicId === pic.id
                            ? 'bg-red-600 text-white animate-pulse ring-2 ring-red-300'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700'
                        }`}
                      >
                        <Mic size={14} /> {isMicListening && activeRecordingPicId === pic.id ? `Recording P${pic.id}...` : `Record P${pic.id}`}
                      </button>
                      {pictureScores[pic.id] && (
                        <div className="text-[11px] font-black text-emerald-950 bg-emerald-100 p-1.5 rounded-lg border border-emerald-300 flex items-center justify-between">
                          <span>P{pic.id} Score:</span>
                          <span>{pictureScores[pic.id].score}% ({pictureScores[pic.id].stars} ★)</span>
                        </div>
                      )}
                    </div>
                  ) : (

                    <span className="text-[10px] font-bold text-indigo-600 italic mt-auto">Intro Story Picture</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Completion Modal for Talkshow & Picture Story */}
      <CompletionModal
        isOpen={isTalkshowEnded || Object.keys(pictureScores).length >= 4}
        onClose={() => {}}
        score={isTalkshowEnded ? 95 : 100}
        stars={3}
        xpEarned={50}
        srsWordsAdded={5}
        activityTitle={isTalkshowEnded ? "Nova Live Talk Show (Speaking Part 4)" : "Picture Story Retell (Speaking Part 3)"}
      />

      {/* Nova Mascot Store Modal */}
      <NovaMascotStore isOpen={showMascotStore} onClose={() => setShowMascotStore(false)} />
    </div>
  );
}

